import React from 'react'
import type { QuoteFile } from 'paintscout'

import { makeStyles, useTheme, useMediaQuery, Grid, FilePreview, Typography, HtmlContent } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useEstimatorQuery } from '@paintscout/api'
import type { PresentationBlockProps } from '../../'
import compareBlockProps from '../../util/compareBlockProps'
import { useQuote } from '../../../context'
import AboutEstimatorPlaceholder from './AboutEstimatorPlaceholder'
import { getSampleEstimator, isPreviewQuote } from './getSampleEstimator'
import { getObjectPosition } from '../../inlineEditable'
import { getFocalCrop } from '@paintscout/util/util'
import PersonOutlineOutlinedIcon from '@material-ui/icons/PersonOutlineOutlined'

// Round photo showing exactly the circle the estimator drew in their profile; capped so it
// can't dwarf the bio. Shared with the sample tile.
const PHOTO_ASPECT = 1
const PHOTO_MAX_WIDTH = 360

const useStyles = makeStyles(
  (theme: Theme) => {
    return {
      root: {},
      content: {},
      photoFrame: {
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: PHOTO_MAX_WIDTH,
        margin: '0 auto',
        aspectRatio: `${PHOTO_ASPECT}`,
        borderRadius: '50%'
      },
      // positioned inline by getFocalCrop: zoomed so the estimator's face circle fills the frame
      photo: {
        position: 'absolute'
      },
      image: {
        width: '100%',
        height: '100%',
        minHeight: 0,
        padding: 0,
        borderRadius: 0,
        // `& img` outranks FilePreview's `fileRoot` (objectFit: contain, natural size)
        '& img': {
          width: '100%',
          height: '100%',
          maxWidth: 'none',
          maxHeight: 'none',
          objectFit: 'cover',
          // only matters when the photo's size is unknown and the crop falls back to cover
          objectPosition: 'var(--about-estimator-object-position, 50% 50%)'
        }
      },
      signature: {
        maxHeight: 50,
        width: 'auto'
      },
      sampleImage: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        aspectRatio: `${PHOTO_ASPECT}`,
        maxWidth: PHOTO_MAX_WIDTH,
        margin: '0 auto',
        borderRadius: '50%',
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.grey[400],
        '& svg': {
          fontSize: 96
        }
      }
    }
  },
  { name: 'AboutEstimatorBlock' }
)

/** Places the photo in the round frame so it shows exactly the face circle from the estimator's profile */
function getPhotoStyle(image: QuoteFile | null | undefined): React.CSSProperties {
  const crop = getFocalCrop({
    options: image?.presentationOptions,
    imageWidth: image?.width,
    imageHeight: image?.height,
    frameAspect: PHOTO_ASPECT,
    fill: 1
  })
  if (!crop) {
    return {
      left: 0,
      top: 0,
      width: '100%',
      height: '100%',
      ['--about-estimator-object-position' as string]: getObjectPosition(image)
    }
  }
  return { left: `${crop.left}%`, top: `${crop.top}%`, width: `${crop.width}%`, height: `${crop.height}%` }
}

function AboutEstimatorBlock(props: PresentationBlockProps) {
  const classes = useStyles(props)
  const { isPreview, isSettings, objectLabels, WrapperComponent, block } = props

  const { estimator, quote } = useQuote()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const responsiveSpacing = isMobile ? 3 : 6
  const { data: estimatorData } = useEstimatorQuery({
    // skip when context already provides an estimator (always the case in customer-view,
    // where the view graphql schema doesn't expose this query)
    skip: !quote?.owner?.id || !!estimator,
    fetchPolicy: 'cache-and-network',
    variables: { id: quote?.owner?.id, quoteId: quote?._id }
  })
  const fetchedEstimator = estimatorData?.estimator

  let fields = {
    name: '',
    title: '',
    description: '',
    signature: '',
    image: null
  }

  // Ported v1 precedence, kept deliberately: a context estimator only counts when it has a
  // real description or an image; otherwise (or with no context estimator at all) the
  // fetched profile wins wholesale. Partial merging of the two sources is intentionally avoided.
  if ((estimator?.description?.length > 1 && estimator.description !== '<p></p><p></p>') || estimator?.image) {
    fields = {
      name: estimator?.name ?? '',
      title: estimator?.title ?? quote?.owner?.title ?? '',
      description: estimator?.description ?? '',
      signature: estimator?.signature ?? '',
      image: estimator?.image ?? null
    }
  }
  if (!estimator) {
    fields = {
      name: fetchedEstimator?.name ?? '',
      title: fetchedEstimator?.title ?? quote?.owner?.title ?? '',
      description: fetchedEstimator?.description ?? '',
      signature: fetchedEstimator?.signature ?? '',
      image: fetchedEstimator?.image ?? null
    }
  }

  // In the presentation editor there is no quote/estimator context; always show the placeholder
  if (isSettings) {
    return (
      <WrapperComponent {...props}>
        <AboutEstimatorPlaceholder objectLabels={objectLabels} showProfileLink={!isPreview} />
      </WrapperComponent>
    )
  }

  // The template preview's sample quote has no estimator; fill in a dummy one, like the sample
  // data the Work Order block shows
  const isSample = isPreview && isPreviewQuote(quote)
  if (isSample) {
    fields = { ...getSampleEstimator(quote), signature: '', image: null }
  }

  return (
    <WrapperComponent {...props}>
      <Grid container spacing={responsiveSpacing} alignItems={'center'}>
        {block?.title && (
          <Grid item md={12}>
            <Typography variant={'h2'}>{block.title}</Typography>
          </Grid>
        )}
        <Grid item md={12}>
          {/* sm={6}, not md: the theme's md (958px) is wider than the iPad preview (834px) */}
          <Grid container spacing={3} alignItems={'center'}>
            {fields?.image && (
              <Grid item xs={12} sm={6}>
                <div className={classes.photoFrame}>
                  <div className={classes.photo} style={getPhotoStyle(fields.image)}>
                    <FilePreview file={fields.image} classes={{ root: classes.image }} />
                  </div>
                </div>
              </Grid>
            )}
            {isSample && (
              <Grid item xs={12} sm={6}>
                <div className={classes.sampleImage} aria-hidden="true">
                  <PersonOutlineOutlinedIcon />
                </div>
              </Grid>
            )}
            {fields?.description && (
              <Grid item xs={12} sm={fields?.image || isSample ? 6 : 12}>
                <Grid container spacing={3}>
                  {fields?.name && (
                    <Grid item md={12}>
                      <Typography variant={'h3'}>{fields.name}</Typography>
                      {fields?.title && <Typography variant={'h4'}>{fields.title}</Typography>}
                    </Grid>
                  )}
                  <Grid item md={12}>
                    <HtmlContent content={fields.description} classes={{ root: classes.content }} />
                  </Grid>
                  {fields?.signature && (
                    <Grid item md={12}>
                      <img
                        src={fields.signature}
                        alt={`${fields.name || 'Estimator'} signature`}
                        className={classes.signature}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </WrapperComponent>
  )
}

export default React.memo(AboutEstimatorBlock, (prevProps, nextProps) => {
  return compareBlockProps(prevProps, nextProps)
})
